import { jsx as _jsx } from "react/jsx-runtime";
import { DEFAULT_WELCOME_TEMPLATE, displayToTemplate, hasWelcomePlaceholders, replaceWelcomePlaceholders } from '@application/utilities/welcome-text-placeholders';
import { applyMissingEmailPresetFormats, EMAIL_EDITOR_PRESET_STYLES, EMAIL_EDITOR_SIZE_OPTIONS, EMAIL_FONT_FAMILY, flattenEmailHeadingParagraphs, flattenEmailSingleLineParagraphs, getEmailLineHeightForSize, htmlContainsInlineFontSize, isSingleLineEmailEditorPreset, needsEmailSingleLineCollapse } from '@application/utilities/email-editor-typography';
import { captureScrollPositions, enterEmailPresetProgrammaticSync, leaveEmailPresetProgrammaticSync, restoreScrollPositionsDeferred } from '@application/utilities/email-editor-scroll-guard';
import { alpha, Box, useTheme } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill, { Quill } from 'react-quill';
function isExternalFormFieldFocused(editorRoot) {
    const active = document.activeElement;
    return ((active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) &&
        !editorRoot.contains(active));
}
function isParagraphEmpty(paragraph) {
    const text = (paragraph.textContent ?? '').replace(/\u200B/g, '').trim();
    if (text !== '')
        return false;
    const innerWithoutBreaks = paragraph.innerHTML.replace(/<br\s*\/?>/gi, '').trim();
    return innerWithoutBreaks === '' || /^<span[^>]*>\s*<\/span>$/i.test(innerWithoutBreaks);
}
function pruneEmptyParagraphs(editorRoot) {
    Array.from(editorRoot.querySelectorAll('p')).forEach((paragraph) => {
        if (isParagraphEmpty(paragraph)) {
            paragraph.remove();
        }
    });
}
function syncPlaceholderParagraphsInPlace(editorRoot, targetHtml) {
    const targetParagraphs = Array.from(new DOMParser().parseFromString(targetHtml, 'text/html').body.querySelectorAll('p')).filter((p) => !isParagraphEmpty(p));
    if (targetParagraphs.length === 0)
        return false;
    const targetSerialized = targetParagraphs.map((p) => p.outerHTML).join('');
    const liveParagraphs = Array.from(editorRoot.querySelectorAll('p'));
    const hasEmptyLiveParagraph = liveParagraphs.some((p) => isParagraphEmpty(p));
    const liveContent = liveParagraphs
        .filter((p) => !isParagraphEmpty(p))
        .map((p) => p.innerHTML)
        .join('\u0001');
    const targetContent = targetParagraphs.map((p) => p.innerHTML).join('\u0001');
    if (!hasEmptyLiveParagraph &&
        liveParagraphs.length === targetParagraphs.length &&
        liveContent === targetContent) {
        return false;
    }
    if (!hasEmptyLiveParagraph &&
        liveParagraphs.length === targetParagraphs.length &&
        liveContent !== targetContent) {
        let changed = false;
        liveParagraphs.forEach((liveP, index) => {
            const targetInner = targetParagraphs[index].innerHTML;
            if (liveP.innerHTML !== targetInner) {
                liveP.innerHTML = targetInner;
                changed = true;
            }
        });
        return changed;
    }
    editorRoot.innerHTML = targetSerialized;
    return true;
}
let styleAttributorsRegistered = false;
let sizePickerCssInjected = false;
function ensureStyleAttributorsRegistered() {
    if (styleAttributorsRegistered)
        return;
    const Parchment = Quill.import('parchment');
    const AlignStyle = new Parchment.Attributor.Style('align', 'text-align', {
        scope: Parchment.Scope.BLOCK,
        whitelist: ['left', 'center', 'right', 'justify']
    });
    const FontStyle = new Parchment.Attributor.Style('font', 'font-family', {
        scope: Parchment.Scope.INLINE
    });
    const SizeStyle = new Parchment.Attributor.Style('size', 'font-size', {
        scope: Parchment.Scope.INLINE,
        whitelist: ['12px', '14px', '16px', '18px', '20px', '24px', '32px', '40px', '42px']
    });
    const ColorStyle = new Parchment.Attributor.Style('color', 'color', {
        scope: Parchment.Scope.INLINE
    });
    const BackgroundStyle = new Parchment.Attributor.Style('background', 'background-color', {
        scope: Parchment.Scope.INLINE
    });
    const LineHeightStyle = new Parchment.Attributor.Style('lineheight', 'line-height', {
        scope: Parchment.Scope.INLINE,
        whitelist: ['16px', '18px', '20px', '22px', '24px', '30px', '32px', '40px', '48px', '50px']
    });
    // Override Quill's default class-based attributors for these formats.
    Quill.register(AlignStyle, true);
    Quill.register(FontStyle, true);
    Quill.register(SizeStyle, true);
    Quill.register(ColorStyle, true);
    Quill.register(BackgroundStyle, true);
    Quill.register(LineHeightStyle, true);
    // Quill's default Icons use '' for left align; we use 'left' so HTML gets text-align:left.
    // Map 'left' to the same icon/label as '' so the dropdown doesn't show "undefined".
    const Icons = Quill.import('ui/icons');
    if (Icons?.align) {
        Icons.align['left'] = Icons.align[''] ?? Icons.align['left'];
    }
    styleAttributorsRegistered = true;
}
function ensureSizePickerCssInjected() {
    if (sizePickerCssInjected)
        return;
    if (typeof document === 'undefined') {
        // SSR safeguard
        return;
    }
    const style = document.createElement('style');
    style.textContent = `
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before { content: '12px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before { content: '14px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before { content: '16px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before { content: '18px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before { content: '24px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before { content: '32px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before { content: '20px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="40px"]::before { content: '40px'; }
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="42px"]::before { content: '42px'; }

.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: '20px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="40px"]::before { content: '40px'; }
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="42px"]::before { content: '42px'; }
`;
    document.head.appendChild(style);
    sizePickerCssInjected = true;
}
const TextEditor = forwardRef(({ value = '', onChange, placeholder = '', errorMessage, enableImageUpload = false, onImageUpload, readOnly = false, variant = 'blue', centerVariant = false, placeholderValues, useStyleAttributors = false, onFocus: onFocusProp, hideToolbar = false, showLinkTool = false, placeholderColor, emailEditorPreset, maxCharacters, forceSyncToken }, ref) => {
    if (useStyleAttributors) {
        ensureStyleAttributorsRegistered();
        ensureSizePickerCssInjected();
    }
    const emailPresetStyles = emailEditorPreset
        ? EMAIL_EDITOR_PRESET_STYLES[emailEditorPreset]
        : null;
    const isSingleLineEmailEditor = isSingleLineEmailEditorPreset(emailEditorPreset);
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const quillRef = useRef(null);
    const isProgrammaticInsertRef = useRef(false);
    // Derived once per render (not inside an effect) so every effect below sees the same
    // isForcedSync value for this render — computing it inside one effect and reusing a ref
    // from another would race, since layout effects commit before effects.
    const prevForceSyncTokenRef = useRef(forceSyncToken);
    const isForcedSync = forceSyncToken !== undefined && prevForceSyncTokenRef.current !== forceSyncToken;
    useEffect(() => {
        prevForceSyncTokenRef.current = forceSyncToken;
    });
    const hasUserSetAlignment = useRef(false);
    const hasUserSetSize = useRef(false);
    const isInitializing = useRef(false);
    const formatTimeoutRef = useRef(null);
    const changeTimeoutRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const emailPresetSyncedValueRef = useRef(undefined);
    const syncEmailPresetFormats = useCallback((notifyChange = false) => {
        if (!emailEditorPreset || !quillRef.current || readOnly)
            return;
        const editor = quillRef.current.getEditor();
        if (!editor)
            return;
        isInitializing.current = true;
        const changed = applyMissingEmailPresetFormats(editor, emailEditorPreset, {
            respectUserSize: hasUserSetSize.current,
            respectUserAlign: hasUserSetAlignment.current
        });
        isInitializing.current = false;
        if (changed && notifyChange) {
            const html = editor.root.innerHTML;
            if (placeholderValues) {
                onChangeRef.current?.(displayToTemplate(html, placeholderValues), 'preset');
            }
            else {
                onChangeRef.current?.(html, 'preset');
            }
        }
    }, [emailEditorPreset, readOnly, placeholderValues]);
    const refreshEditorToolbar = useCallback((editor) => {
        const range = editor.getSelection();
        if (!range)
            return;
        editor.setSelection(range.index, range.length, 'silent');
    }, []);
    const sizeLabels = useMemo(() => ({
        normal: t('editor.size.normal', 'Normal'),
        small: t('editor.size.small', 'Small'),
        large: t('editor.size.large', 'Large'),
        huge: t('editor.size.huge', 'Huge')
    }), [i18n.language, t]);
    const linkLabels = useMemo(() => ({
        enterLink: t('editor.link.enterLink', 'Enter link:'),
        save: t('editor.link.save', 'Save')
    }), [i18n.language, t]);
    const displayValue = useMemo(() => {
        if (!placeholderValues)
            return value;
        const normalizedValue = typeof value === 'string' ? value.trim() : '';
        if (normalizedValue !== '' && value) {
            if (hasWelcomePlaceholders(value)) {
                return replaceWelcomePlaceholders(value, placeholderValues);
            }
            return value;
        }
        return replaceWelcomePlaceholders(DEFAULT_WELCOME_TEMPLATE, placeholderValues);
    }, [value, placeholderValues]);
    const effectiveValue = placeholderValues ? displayValue : value;
    const isEditorFocusedRef = useRef(false);
    const [quillValue, setQuillValue] = useState(() => effectiveValue ?? '');
    const latestPlaceholderDisplayRef = useRef(effectiveValue ?? '');
    latestPlaceholderDisplayRef.current = effectiveValue ?? '';
    useImperativeHandle(ref, () => ({
        insertAtCursor(html) {
            const editor = quillRef.current?.getEditor();
            if (!editor || readOnly)
                return;
            const scrollSnapshot = captureScrollPositions(editor.root);
            isProgrammaticInsertRef.current = true;
            isEditorFocusedRef.current = true;
            const range = editor.getSelection(true) || {
                index: Math.max(0, editor.getLength() - 1),
                length: 0
            };
            const beforeLen = editor.getLength();
            editor.clipboard.dangerouslyPasteHTML(range.index, html, 'silent');
            const afterLen = editor.getLength();
            const insertedLen = Math.max(0, afterLen - beforeLen);
            const newIndex = range.index + insertedLen;
            editor.setSelection(newIndex, 0, 'silent');
            const newHtml = editor.root.innerHTML;
            setQuillValue(newHtml);
            emailPresetSyncedValueRef.current = newHtml;
            if (onChange && !placeholderValues) {
                onChange(newHtml, 'user');
            }
            else if (onChange && placeholderValues) {
                onChange(displayToTemplate(newHtml, placeholderValues), 'user');
            }
            editor.root.focus({ preventScroll: true });
            restoreScrollPositionsDeferred(scrollSnapshot);
            window.requestAnimationFrame(() => {
                editor.root.focus({ preventScroll: true });
                restoreScrollPositionsDeferred(scrollSnapshot);
                window.requestAnimationFrame(() => {
                    isProgrammaticInsertRef.current = false;
                });
            });
        }
    }), [readOnly, onChange, placeholderValues]);
    useEffect(() => {
        if (isProgrammaticInsertRef.current)
            return;
        if (placeholderValues) {
            if (!isEditorFocusedRef.current) {
                return;
            }
            const editor = quillRef.current?.getEditor();
            const editorHtml = editor?.root?.innerHTML ?? '';
            if (editorHtml && effectiveValue === editorHtml) {
                setQuillValue(effectiveValue ?? '');
            }
            return;
        }
        // A forced sync (a different document was just loaded, e.g. switching language) always
        // overwrites, even if this editor currently has focus.
        if (isForcedSync || !isEditorFocusedRef.current) {
            if (isForcedSync) {
                isEditorFocusedRef.current = false;
            }
            setQuillValue(effectiveValue ?? '');
            return;
        }
        const editor = quillRef.current?.getEditor();
        const editorHtml = editor?.root?.innerHTML ?? '';
        if (editorHtml && effectiveValue === editorHtml) {
            setQuillValue(effectiveValue ?? '');
        }
    }, [effectiveValue, placeholderValues, isForcedSync]);
    const handleChange = useMemo(() => {
        if (!placeholderValues || !onChange)
            return onChange;
        return (content) => {
            onChange(displayToTemplate(content, placeholderValues));
        };
    }, [placeholderValues, onChange]);
    const handleQuillChange = useCallback((content, _delta, source) => {
        if (isInitializing.current)
            return;
        if (emailEditorPreset && source !== 'user')
            return;
        setQuillValue(content);
        // Email presets have their own text-change pipeline that applies preset formatting,
        // single-line collapsing, and character limits before syncing upward. If we also push
        // ReactQuill's raw content here, the preview can briefly show over-limit text and then
        // snap back once the post-processing runs.
        if (emailEditorPreset)
            return;
        const handler = handleChange ?? onChange;
        if (!handler)
            return;
        handler(content, 'user');
    }, [emailEditorPreset, handleChange, onChange]);
    const handleEditorBlur = useCallback(() => {
        if (isProgrammaticInsertRef.current)
            return;
        isEditorFocusedRef.current = false;
        setQuillValue(effectiveValue ?? '');
    }, [effectiveValue]);
    const enforceCharacterLimit = useCallback(() => {
        if (!quillRef.current || !maxCharacters || maxCharacters <= 0)
            return false;
        const editor = quillRef.current.getEditor();
        if (!editor)
            return false;
        const currentLength = Math.max(0, editor.getLength() - 1);
        if (currentLength <= maxCharacters)
            return false;
        editor.deleteText(maxCharacters, currentLength - maxCharacters, 'silent');
        return true;
    }, [maxCharacters]);
    const notifyEmailEditorUserChange = useCallback(() => {
        if (isInitializing.current || !quillRef.current)
            return;
        const editor = quillRef.current.getEditor();
        if (!editor)
            return;
        enforceCharacterLimit();
        let html = editor.root.innerHTML;
        if (isSingleLineEmailEditorPreset(emailEditorPreset) && needsEmailSingleLineCollapse(html)) {
            const selection = editor.getSelection();
            const wasAtEnd = !selection || selection.index >= Math.max(0, editor.getLength() - 2);
            const flattened = flattenEmailSingleLineParagraphs(html);
            if (flattened !== html) {
                isInitializing.current = true;
                const len = editor.getLength();
                editor.deleteText(0, len > 0 ? len : 1);
                editor.clipboard.dangerouslyPasteHTML(0, flattened, 'silent');
                syncEmailPresetFormats(false);
                if (wasAtEnd) {
                    editor.setSelection(Math.max(0, editor.getLength() - 1), 0, 'silent');
                }
                else if (selection) {
                    editor.setSelection(Math.min(selection.index, Math.max(0, editor.getLength() - 1)), 0, 'silent');
                }
                isInitializing.current = false;
                html = flattened;
            }
        }
        setQuillValue(html);
        onChangeRef.current?.(html, 'user');
        refreshEditorToolbar(editor);
    }, [emailEditorPreset, enforceCharacterLimit, refreshEditorToolbar, syncEmailPresetFormats]);
    useEffect(() => {
        if (!emailEditorPreset || readOnly || !quillRef.current)
            return;
        const editor = quillRef.current.getEditor();
        if (!editor)
            return;
        const handleEditorTextChange = (_delta, _old, source) => {
            if (source !== 'user' || isInitializing.current)
                return;
            notifyEmailEditorUserChange();
        };
        editor.on('text-change', handleEditorTextChange);
        return () => {
            editor.off('text-change', handleEditorTextChange);
        };
    }, [emailEditorPreset, readOnly, notifyEmailEditorUserChange]);
    useEffect(() => {
        emailPresetSyncedValueRef.current = undefined;
    }, [emailEditorPreset]);
    useLayoutEffect(() => {
        if (!emailEditorPreset || readOnly)
            return;
        if (isProgrammaticInsertRef.current)
            return;
        if (!isForcedSync && emailPresetSyncedValueRef.current === effectiveValue)
            return;
        const runSync = () => {
            if (!quillRef.current)
                return false;
            const editor = quillRef.current.getEditor();
            if (!editor?.clipboard)
                return false;
            // A forced sync means an entirely different document was just loaded (e.g. switching
            // language), so we always overwrite the editor's content, even if it currently has
            // focus — unlike a live placeholder update, there is no in-progress edit to protect.
            const hadFocus = !isForcedSync &&
                (isEditorFocusedRef.current || editor.root.contains(document.activeElement));
            if (isForcedSync) {
                isEditorFocusedRef.current = false;
            }
            if (!hadFocus) {
                enterEmailPresetProgrammaticSync(editor.root);
            }
            try {
                let incomingValue = effectiveValue ?? '';
                if (emailEditorPreset === 'heading') {
                    incomingValue = flattenEmailHeadingParagraphs(incomingValue);
                }
                else if (isSingleLineEmailEditorPreset(emailEditorPreset)) {
                    incomingValue = flattenEmailSingleLineParagraphs(incomingValue);
                }
                if (!hadFocus && editor.root.innerHTML !== incomingValue) {
                    isInitializing.current = true;
                    const len = editor.getLength();
                    editor.deleteText(0, len > 0 ? len : 1);
                    editor.clipboard.dangerouslyPasteHTML(0, incomingValue, 'silent');
                    isInitializing.current = false;
                    hasUserSetSize.current = false;
                    hasUserSetAlignment.current = false;
                }
                syncEmailPresetFormats(false);
                const displayHtml = editor.root.innerHTML;
                const needsFormattedDisplay = !hasUserSetSize.current &&
                    !(isSingleLineEmailEditor && hadFocus) &&
                    (displayHtml !== incomingValue || !htmlContainsInlineFontSize(displayHtml));
                // Keep React's controlled quillValue state in sync with the DOM we just wrote via
                // dangerouslyPasteHTML — otherwise ReactQuill's next re-render reconciles the editor
                // back to the stale quillValue state, silently reverting this sync.
                if (!hadFocus) {
                    setQuillValue(displayHtml);
                }
                if (needsFormattedDisplay) {
                    isInitializing.current = true;
                    onChangeRef.current?.(displayHtml, 'preset');
                    isInitializing.current = false;
                    emailPresetSyncedValueRef.current = displayHtml;
                }
                else {
                    emailPresetSyncedValueRef.current = incomingValue;
                }
            }
            finally {
                if (!hadFocus) {
                    leaveEmailPresetProgrammaticSync();
                }
            }
            return true;
        };
        if (runSync())
            return;
        requestAnimationFrame(() => {
            runSync();
        });
    }, [effectiveValue, emailEditorPreset, readOnly, syncEmailPresetFormats, isForcedSync]);
    // ReactQuill does not update its DOM when the value prop changes; we must sync
    // when effectiveValue changes (e.g. date/time/location filled in other fields).
    // While another form field is focused, patch only changed paragraphs in-place so
    // location/date/time update live without stealing focus via a full Quill rewrite.
    useEffect(() => {
        if (!placeholderValues || !quillRef.current)
            return;
        const editor = quillRef.current.getEditor();
        if (!editor?.clipboard)
            return;
        const syncPlaceholderDisplay = () => {
            if (isEditorFocusedRef.current)
                return;
            if (editor.root.contains(document.activeElement))
                return;
            const targetHtml = latestPlaceholderDisplayRef.current;
            const activeEl = document.activeElement;
            const currentHtml = editor.root.innerHTML;
            if (currentHtml !== targetHtml) {
                const len = editor.getLength();
                editor.deleteText(0, len > 0 ? len : 1);
                editor.clipboard.dangerouslyPasteHTML(0, targetHtml, 'silent');
                setQuillValue(targetHtml);
                if (centerVariant && !hasUserSetAlignment.current) {
                    requestAnimationFrame(() => {
                        const ed = quillRef.current?.getEditor();
                        if (ed) {
                            const docLen = ed.getLength();
                            if (docLen > 1) {
                                ed.formatLine(0, docLen, 'align', 'center', 'silent');
                                if (placeholderValues && onChange) {
                                    onChange(displayToTemplate(ed.root.innerHTML, placeholderValues));
                                }
                            }
                        }
                        if (activeEl?.focus)
                            activeEl.focus();
                    });
                }
                else if (activeEl?.focus) {
                    requestAnimationFrame(() => activeEl.focus());
                }
            }
            else if (centerVariant && !hasUserSetAlignment.current) {
                const ed = quillRef.current?.getEditor();
                if (ed) {
                    const len = ed.getLength();
                    if (len > 1) {
                        ed.formatLine(0, len, 'align', 'center', 'silent');
                        if (placeholderValues && onChange) {
                            onChange(displayToTemplate(ed.root.innerHTML, placeholderValues));
                        }
                    }
                }
            }
        };
        if (isExternalFormFieldFocused(editor.root)) {
            if (syncPlaceholderParagraphsInPlace(editor.root, latestPlaceholderDisplayRef.current)) {
                pruneEmptyParagraphs(editor.root);
                if (centerVariant && !hasUserSetAlignment.current) {
                    const len = editor.getLength();
                    if (len > 1) {
                        editor.formatLine(0, len, 'align', 'center', 'silent');
                    }
                }
                pruneEmptyParagraphs(editor.root);
                setQuillValue(editor.root.innerHTML);
                return;
            }
            const activeField = document.activeElement;
            const handleBlur = () => {
                activeField.removeEventListener('blur', handleBlur);
                syncPlaceholderDisplay();
            };
            activeField.addEventListener('blur', handleBlur);
            return () => activeField.removeEventListener('blur', handleBlur);
        }
        syncPlaceholderDisplay();
    }, [effectiveValue, placeholderValues, centerVariant, onChange]);
    useEffect(() => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            if (editor?.root) {
                editor.root.dataset.placeholder = placeholder || '';
            }
        }
    }, [placeholder]);
    // Apply center by default when centerVariant is true and editor has content (e.g. new event welcome template).
    // Do not use setSelection so we never steal focus from other fields (e.g. location, time).
    // Do not run when editor has focus so we never select-all while the user is typing.
    useEffect(() => {
        if (!centerVariant || !quillRef.current || readOnly)
            return;
        const editor = quillRef.current.getEditor();
        if (!editor || hasUserSetAlignment.current)
            return;
        if (editor.root.contains(document.activeElement))
            return;
        const timer = setTimeout(() => {
            const ed = quillRef.current?.getEditor();
            if (!ed)
                return;
            if (isExternalFormFieldFocused(ed.root))
                return;
            const len = ed.getLength();
            if (len > 1) {
                ed.formatLine(0, len, 'align', 'center', 'silent');
                if (placeholderValues && onChange) {
                    onChange(displayToTemplate(ed.root.innerHTML, placeholderValues));
                }
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [centerVariant, readOnly, effectiveValue, placeholderValues, onChange]);
    const handleEditorFocusOrClick = () => {
        isEditorFocusedRef.current = true;
        onFocusProp?.();
        if (centerVariant && quillRef.current && !readOnly) {
            const editor = quillRef.current.getEditor();
            if (!editor)
                return;
            if (!hasUserSetAlignment.current) {
                const len = editor.getLength();
                if (len > 1) {
                    editor.formatLine(0, len, 'align', 'center', 'silent');
                }
                else if (len > 0) {
                    editor.format('align', 'center', 'silent');
                }
            }
            if ((editor.getText().trim() === '' || !value) && !hasUserSetSize.current) {
                editor.format('size', 'large', 'silent');
            }
        }
    };
    useLayoutEffect(() => {
        if (centerVariant && quillRef.current && !readOnly) {
            const editor = quillRef.current.getEditor();
            if (editor) {
                const handleSelectionChange = (range) => {
                    const text = editor.getText().trim();
                    if (text === '' && range && range.length === 0) {
                        if (!hasUserSetAlignment.current) {
                            editor.format('align', 'center', 'silent');
                        }
                        if (!hasUserSetSize.current) {
                            editor.format('size', 'large', 'silent');
                        }
                    }
                    if (range && range.length > 0) {
                        const formats = editor.getFormat(range);
                        if (formats.align !== undefined && formats.align !== 'center') {
                            hasUserSetAlignment.current = true;
                        }
                        if (formats.size !== undefined && formats.size !== 'large') {
                            hasUserSetSize.current = true;
                        }
                    }
                };
                const handleTextChange = (delta, oldDelta, source) => {
                    if (source !== 'user' || isInitializing.current)
                        return;
                    const text = editor.getText().trim();
                    if (formatTimeoutRef.current) {
                        clearTimeout(formatTimeoutRef.current);
                    }
                    if (text === '') {
                        hasUserSetAlignment.current = false;
                        hasUserSetSize.current = false;
                        if (!hasUserSetAlignment.current) {
                            editor.format('align', 'center', 'silent');
                        }
                        if (!hasUserSetSize.current) {
                            editor.format('size', 'large', 'silent');
                        }
                    }
                    else if (text.length <= 5 && !value) {
                        const selection = editor.getSelection();
                        if (selection) {
                            const formats = editor.getFormat(selection);
                            if (!hasUserSetAlignment.current &&
                                (formats.align === undefined || formats.align === false)) {
                                editor.format('align', 'center', 'silent');
                            }
                            if (!hasUserSetSize.current &&
                                (formats.size === undefined ||
                                    formats.size === false ||
                                    formats.size === 'normal')) {
                                editor.format('size', 'large', 'silent');
                            }
                        }
                    }
                };
                const toolbar = editor.getModule('toolbar');
                if (toolbar && toolbar.container) {
                    const alignButtons = toolbar.container.querySelectorAll('.ql-align');
                    alignButtons.forEach((button) => {
                        button.addEventListener('click', () => {
                            hasUserSetAlignment.current = true;
                        });
                    });
                    const sizePicker = toolbar.container.querySelector('.ql-size');
                    if (sizePicker) {
                        sizePicker.addEventListener('change', () => {
                            hasUserSetSize.current = true;
                        });
                        sizePicker.addEventListener('click', () => {
                            hasUserSetSize.current = true;
                        });
                    }
                }
                editor.on('selection-change', handleSelectionChange);
                editor.on('text-change', handleTextChange);
                return () => {
                    if (formatTimeoutRef.current) {
                        clearTimeout(formatTimeoutRef.current);
                    }
                    editor.off('selection-change', handleSelectionChange);
                    editor.off('text-change', handleTextChange);
                };
            }
        }
    }, [centerVariant, readOnly]);
    useLayoutEffect(() => {
        if (!emailEditorPreset || !quillRef.current || readOnly)
            return;
        const editor = quillRef.current.getEditor();
        if (!editor)
            return;
        const handleSelectionChange = (range) => {
            if (!range || isInitializing.current)
                return;
            const formats = editor.getFormat(range);
            if (formats.size && formats.size !== EMAIL_EDITOR_PRESET_STYLES[emailEditorPreset].defaultSize) {
                hasUserSetSize.current = true;
            }
            if (formats.align &&
                formats.align !== EMAIL_EDITOR_PRESET_STYLES[emailEditorPreset].textAlign) {
                hasUserSetAlignment.current = true;
            }
            if (!hasUserSetSize.current || !hasUserSetAlignment.current) {
                syncEmailPresetFormats(false);
            }
        };
        const toolbar = editor.getModule('toolbar');
        if (toolbar?.container) {
            const sizePicker = toolbar.container.querySelector('.ql-size');
            if (sizePicker) {
                sizePicker.addEventListener('click', () => {
                    hasUserSetSize.current = true;
                });
            }
            const alignButtons = toolbar.container.querySelectorAll('.ql-align');
            alignButtons.forEach((button) => {
                button.addEventListener('click', () => {
                    hasUserSetAlignment.current = true;
                });
            });
        }
        editor.on('selection-change', handleSelectionChange);
        return () => {
            editor.off('selection-change', handleSelectionChange);
        };
    }, [emailEditorPreset, readOnly, syncEmailPresetFormats]);
    const toolbarItems = useMemo(() => {
        const items = [];
        if (!emailEditorPreset) {
            items.push([{ font: [] }]);
        }
        items.push([
            {
                size: useStyleAttributors
                    ? emailEditorPreset
                        ? [...EMAIL_EDITOR_SIZE_OPTIONS]
                        : ['12px', '14px', '16px', '18px', '24px', '32px', '40px']
                    : ['small', false, 'large', 'huge']
            }
        ]);
        items.push(['bold', 'italic', 'underline']);
        items.push([{ list: 'ordered' }, { list: 'bullet' }]);
        items.push([{ align: useStyleAttributors ? ['left', 'center', 'right', 'justify'] : [] }]);
        if (enableImageUpload) {
            items.push(['image']);
        }
        items.push(['clean']);
        return items;
    }, [emailEditorPreset, useStyleAttributors, enableImageUpload]);
    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
            if (!input.files?.length)
                return;
            const file = input.files[0];
            try {
                if (!onImageUpload)
                    throw new Error('No upload handler provided');
                const url = await onImageUpload(file);
                const editor = quillRef.current?.getEditor();
                if (editor) {
                    const range = editor.getSelection(true);
                    editor.insertEmbed(range.index, 'image', url);
                    editor.setSelection(range.index + 1, range.length);
                }
            }
            catch (err) {
                console.error('Image upload failed:', err);
            }
        };
    };
    const singleLineKeyboardBindings = useMemo(() => ({
        enter: {
            key: 'Enter',
            handler: () => false
        },
        linebreak: {
            key: 'Enter',
            shiftKey: true,
            handler: () => false
        }
    }), []);
    const modules = useMemo(() => {
        const keyboardModule = isSingleLineEmailEditorPreset(emailEditorPreset)
            ? { keyboard: { bindings: singleLineKeyboardBindings } }
            : {};
        if (readOnly)
            return { toolbar: false, ...keyboardModule };
        if (hideToolbar && showLinkTool) {
            return { toolbar: { container: [['link']] }, ...keyboardModule };
        }
        if (hideToolbar)
            return { toolbar: false, ...keyboardModule };
        return {
            ...keyboardModule,
            toolbar: {
                container: toolbarItems,
                handlers: {
                    ...(enableImageUpload ? { image: imageHandler } : {}),
                    ...(emailEditorPreset
                        ? {
                            size: function (value) {
                                const quill = this.quill;
                                hasUserSetSize.current = true;
                                quill.format('size', value || false);
                                if (value) {
                                    const lineHeight = getEmailLineHeightForSize(value);
                                    if (lineHeight) {
                                        quill.format('lineheight', lineHeight);
                                    }
                                }
                                else {
                                    quill.format('lineheight', false);
                                }
                                notifyEmailEditorUserChange();
                            },
                            align: function (value) {
                                const quill = this.quill;
                                hasUserSetAlignment.current = true;
                                quill.format('align', value || false);
                                notifyEmailEditorUserChange();
                            }
                        }
                        : {})
                }
            }
        };
    }, [
        readOnly,
        hideToolbar,
        showLinkTool,
        enableImageUpload,
        toolbarItems,
        emailEditorPreset,
        notifyEmailEditorUserChange,
        singleLineKeyboardBindings
    ]);
    useEffect(() => {
        if (!hideToolbar || readOnly || !quillRef.current)
            return;
        const editor = quillRef.current.getEditor();
        if (!editor)
            return;
        const autoLinkUrls = (_delta, _old, source) => {
            if (source !== 'user')
                return;
            const text = editor.getText();
            const urlRegex = /https?:\/\/[^\s]+/g;
            let match;
            while ((match = urlRegex.exec(text)) !== null) {
                const existing = editor.getFormat(match.index, match[0].length);
                if (!existing.link) {
                    editor.formatText(match.index, match[0].length, 'link', match[0], 'api');
                }
            }
        };
        editor.on('text-change', autoLinkUrls);
        return () => {
            editor.off('text-change', autoLinkUrls);
        };
    }, [hideToolbar, readOnly]);
    const formats = [
        'font',
        'size',
        ...(emailEditorPreset ? ['lineheight'] : []),
        'bold',
        'italic',
        'underline',
        'list',
        'bullet',
        'align',
        'link',
        'clean',
        'image'
    ];
    return (_jsx(Box, { className: 'text-editor-container', width: '100%', onClick: (e) => {
            if (!readOnly) {
                const editor = quillRef.current?.getEditor();
                const quillWrapper = editor?.root?.parentElement?.parentElement;
                if (quillWrapper?.contains(e.target))
                    return;
                editor?.focus();
            }
        }, sx: {
            cursor: readOnly ? 'default' : 'pointer',
            border: (theme) => errorMessage && theme.palette.error.main,
            ...(readOnly
                ? {
                    '.ql-toolbar': {
                        display: 'none'
                    },
                    '.ql-editor ol, .ql-editor ul': {
                        color: theme.palette.common.black
                    },
                    '.ql-container': {
                        border: 'none',
                        backgroundColor: 'transparent',
                        minHeight: 'auto'
                    },
                    '.ql-editor': {
                        padding: 0,
                        // In read-only mode, always use standard disabled text color,
                        // regardless of variant, to match other disabled inputs.
                        color: theme.palette.text.primary,
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block',
                            margin: '8px 0'
                        },
                        '& p': {
                            margin: 0,
                            fontFamily: variant === 'blue' ? undefined : variant === '' ? 'SuisseIntl' : undefined,
                            color: variant === 'blue'
                                ? undefined
                                : variant === '' || variant === 'white'
                                    ? theme.palette.common.black
                                    : undefined
                        },
                        '& .ql-size-small': {
                            fontSize: '12px !important'
                        },
                        '& .ql-size-large': {
                            fontSize: '24px !important'
                        },
                        '& .ql-size-huge': {
                            fontSize: '32px !important'
                        }
                    }
                }
                : {
                    '.ql-toolbar': {
                        ...(hideToolbar && showLinkTool
                            ? {
                                backgroundColor: (theme) => theme.palette.background.paper,
                                borderRadius: '14px 14px 0 0',
                                border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                                borderBottom: 'none',
                                padding: '4px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                button: {
                                    borderRadius: '6px',
                                    '&:hover': {
                                        backgroundColor: (theme) => theme.palette.grey['500Transparent'][16],
                                        '& .ql-stroke': { stroke: theme.palette.grey[700] }
                                    },
                                    '&.ql-active': {
                                        '& .ql-stroke': { stroke: theme.palette.grey[700] }
                                    }
                                },
                                '.ql-stroke': { stroke: (theme) => theme.palette.grey[500] }
                            }
                            : {
                                backgroundColor: (theme) => theme.palette.background.paper,
                                borderRadius: '12px 12px 0 0',
                                border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                padding: '8px',
                                button: {
                                    borderRadius: '6px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: (theme) => theme.palette.grey['500Transparent'][16],
                                        '& .ql-stroke': { stroke: theme.palette.grey[700] },
                                        '& .ql-fill': { fill: theme.palette.grey[700] }
                                    },
                                    '&.ql-active': {
                                        backgroundColor: (theme) => theme.palette.grey['500Transparent'][24],
                                        '& .ql-stroke': { stroke: theme.palette.grey[700] },
                                        '& .ql-fill': { fill: theme.palette.grey[700] }
                                    }
                                },
                                '.ql-picker': {
                                    borderRadius: '6px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: (theme) => theme.palette.grey['500Transparent'][16]
                                    }
                                },
                                '.ql-picker-label': {
                                    color: (theme) => theme.palette.text.primary,
                                    '&:hover': { color: theme.palette.grey[700] }
                                },
                                '.ql-picker-label .ql-stroke': { stroke: theme.palette.grey[700] },
                                '.ql-picker-label:hover .ql-stroke': {
                                    stroke: theme.palette.grey[700]
                                }
                            })
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-label::before': {
                        content: JSON.stringify(sizeLabels.normal)
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before': {
                        content: JSON.stringify(sizeLabels.small)
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before': {
                        content: JSON.stringify(sizeLabels.large)
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before': {
                        content: JSON.stringify(sizeLabels.huge)
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-options .ql-picker-item:not([data-value])::before': {
                        content: JSON.stringify(sizeLabels.normal)
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-options .ql-picker-item[data-value="small"]::before': {
                        content: JSON.stringify(sizeLabels.small)
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-options .ql-picker-item[data-value="large"]::before': {
                        content: JSON.stringify(sizeLabels.large)
                    },
                    '.ql-snow .ql-picker.ql-size .ql-picker-options .ql-picker-item[data-value="huge"]::before': {
                        content: JSON.stringify(sizeLabels.huge)
                    },
                    '.ql-container': {
                        border: hideToolbar
                            ? (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`
                            : 'none',
                        borderRadius: hideToolbar && showLinkTool
                            ? '0 0 14px 14px'
                            : hideToolbar
                                ? '14px'
                                : '0 0 12px 12px',
                        backgroundColor: (theme) => theme.palette.common.grey8,
                        minHeight: isSingleLineEmailEditor ? '56px' : '150px'
                    },
                    '.ql-tooltip': {
                        left: '0 !important',
                        right: 'auto'
                    },
                    '.ql-snow .ql-tooltip[data-mode=link]::before': {
                        content: JSON.stringify(linkLabels.enterLink)
                    },
                    '.ql-snow .ql-tooltip.ql-editing a.ql-action::after': {
                        content: JSON.stringify(linkLabels.save)
                    },
                    '.ql-editor': {
                        ...(emailPresetStyles
                            ? {
                                fontFamily: EMAIL_FONT_FAMILY,
                                fontSize: emailPresetStyles.fontSize,
                                lineHeight: emailPresetStyles.lineHeight,
                                textAlign: emailPresetStyles.textAlign,
                                color: emailPresetStyles.color,
                                fontWeight: emailPresetStyles.fontWeight,
                                '& p, & li': {
                                    fontFamily: EMAIL_FONT_FAMILY,
                                    margin: 0,
                                    marginBottom: emailEditorPreset === 'heading' || isSingleLineEmailEditor
                                        ? 0
                                        : '0.65em',
                                    textAlign: emailPresetStyles.textAlign,
                                    ...(emailEditorPreset === 'heading'
                                        ? { lineHeight: emailPresetStyles.lineHeight }
                                        : {}),
                                    ...(isSingleLineEmailEditor
                                        ? {
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere'
                                        }
                                        : {})
                                },
                                '& p:last-child, & li:last-child': {
                                    marginBottom: 0
                                },
                                '& span, & strong, & em, & u': {
                                    fontFamily: EMAIL_FONT_FAMILY
                                }
                            }
                            : theme.typography.body2),
                        padding: '12px',
                        color: emailPresetStyles?.color ?? ((theme) => theme.palette.grey[700]),
                        minHeight: isSingleLineEmailEditor ? '48px' : '150px',
                        height: '100%',
                        cursor: 'text',
                        ...(isSingleLineEmailEditor
                            ? {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }
                            : {}),
                        '&.ql-blank::before': {
                            color: placeholderColor ?? ((theme) => theme.palette.text.disabled),
                            fontStyle: 'normal'
                        },
                        '&:focus.ql-blank::before': {
                            content: "''"
                        },
                        '& .ql-size-small': {
                            fontSize: '12px !important'
                        },
                        '& .ql-size-large': {
                            fontSize: '24px !important'
                        },
                        '& .ql-size-huge': {
                            fontSize: '32px !important'
                        }
                    },
                    '.ql-snow .ql-stroke': {
                        stroke: theme.palette.grey[500]
                    },
                    '.ql-snow .ql-fill': {
                        fill: theme.palette.grey[500]
                    },
                    '.ql-snow .ql-picker': {
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: (theme) => theme.palette.text.primary
                    },
                    '.ql-picker-label:hover': {
                        color: theme.palette.grey[700]
                    },
                    '.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-label': {
                        border: 'none'
                    },
                    '.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options': {
                        bgcolor: (theme) => theme.palette.grey[800],
                        mt: 1,
                        padding: 0,
                        border: 'none',
                        boxShadow: '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D',
                        borderRadius: (theme) => theme.spacing(1.5)
                    },
                    '.ql-snow .ql-picker-options .ql-picker-item': {
                        ...theme.typography.body2,
                        padding: 0.5,
                        color: (theme) => theme.palette.common.white,
                        ':hover': {
                            backgroundColor: (theme) => theme.palette.grey['500Transparent'][16],
                            borderRadius: theme.spacing(1)
                        }
                    },
                    '.ql-picker-item.ql-selected': {
                        backgroundColor: theme.palette.grey['500Transparent'][16],
                        borderRadius: theme.spacing(1)
                    },
                    '.ql-picker-label.ql-active': {
                        color: `${theme.palette.grey[700]} !important`
                    }
                })
        }, children: _jsx(ReactQuill, { ref: quillRef, preserveWhitespace: true, theme: 'snow', value: quillValue, onChange: handleQuillChange, modules: modules, readOnly: readOnly, formats: formats, placeholder: placeholder, className: 'custom-quill-container', onFocus: handleEditorFocusOrClick, onBlur: handleEditorBlur }) }));
});
export default TextEditor;
